import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import AOS from 'aos'
import 'aos/dist/aos.css'
import Navbar from '../Navbar'
import Footer from '../page-main/footer'
import ServiciosList from '../ServiciosList'
import { db } from '../../firebase'
import { collection, getDocs } from 'firebase/firestore'
import { 
  GlobeAltIcon, 
  Cog6ToothIcon, 
  CodeBracketSquareIcon, 
  CloudArrowUpIcon,
  WrenchScrewdriverIcon,
  ArrowLongRightIcon
} from '@heroicons/react/24/outline'

const services = [
  {
    id: 1,
    name: 'WordPress',
    description: 'Desarrollo de sitios web profesionales con WordPress. Diseño personalizado, responsive y optimizado para SEO.',
    icon: GlobeAltIcon,
    features: ['Diseño personalizado', 'SEO optimizado', 'Responsive', 'Panel de administración'],
    whatsappMessage: 'Hola, estoy interesado en el servicio de desarrollo WordPress'
  },
  {
    id: 2,
    name: 'Odoo',
    description: 'Implementación y personalización de Odoo ERP. Gestiona tu empresa con un sistema integral.',
    icon: Cog6ToothIcon,
    features: ['ERP completo', 'Módulos personalizados', 'Integración total', 'Soporte técnico'],
    whatsappMessage: 'Hola, deseo información sobre la implementación de Odoo ERP'
  },
  {
    id: 3,
    name: 'Sistemas a Medida',
    description: 'Desarrollo de software personalizado adaptado a las necesidades específicas de tu negocio.',
    icon: CodeBracketSquareIcon,
    features: ['100% personalizado', 'Escalable', 'Tecnología moderna', 'Integración API'],
    whatsappMessage: 'Hola, me interesa desarrollar un sistema a medida para mi negocio'
  },
  {
    id: 4,
    name: 'Instalación de Software',
    description: 'Instalación profesional de software empresarial. Configuración, migración de datos y puesta en marcha.',
    icon: CloudArrowUpIcon,
    features: ['Instalación profesional', 'Migración de datos', 'Configuración inicial', 'Capacitación'],
    whatsappMessage: 'Hola, necesito ayuda con la instalación de software empresarial'
  },
  {
    id: 5,
    name: 'Actualización y Mantenimiento',
    description: 'Servicio de mantenimiento continuo para tus sistemas. Actualizaciones, monitoreo y soporte técnico.',
    icon: WrenchScrewdriverIcon,
    features: ['Actualizaciones regulares', 'Monitoreo 24/7', 'Respaldos automáticos', 'Soporte prioritario'],
    whatsappMessage: 'Hola, me gustaría contratar el servicio de mantenimiento y actualización'
  }
]

export default function Servicios() {
  const [productosSchema, setProductosSchema] = useState([])
  const isReactSnap = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap'

  useEffect(() => {
    AOS.init({ duration: 800, once: true, easing: 'ease-out-cubic' })
  }, [])

  useEffect(() => {
    if (isReactSnap) return
    const load = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'productos'))
        const prods = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        const schema = prods.map(p => ({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: p.nombre,
          description: p.descripcion,
          image: p.imagen ? [p.imagen] : undefined,
          offers: {
            '@type': 'Offer',
            priceCurrency: 'PEN',
            price: Number(p.precio || 0),
            availability: 'https://schema.org/InStock',
            url: 'https://www.adrox.top/servicios'
          }
        }))
        setProductosSchema(schema)
      } catch (e) { /* ignore */ }
    }
    load()
  }, [isReactSnap])

  const handleServiceClick = (message) => {
    const encodedMessage = encodeURIComponent(message)
    window.open(`https://wa.me/+51933534235?text=${encodedMessage}`, '_blank')
  }

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": services.map((service, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Service",
        "name": service.name,
        "description": service.description,
        "provider": { "@type": "Organization", "name": "Adrox Peru" }
      }
    }))
  }

  return (
    <>
      <Helmet>
        <title>Servicios de Desarrollo de Software | Adrox Peru</title>
        <meta name="description" content="Servicios de desarrollo de software en Perú: WordPress, Odoo ERP, sistemas personalizados, apps móviles. Instalación y mantenimiento profesional." />
        <meta name="keywords" content="desarrollo wordpress, odoo erp, sistemas a medida, instalación software, mantenimiento software, adrox peru" />
        <meta property="og:title" content="Servicios de Desarrollo de Software | Adrox Peru" />
        <meta property="og:description" content="Servicios profesionales de desarrollo: WordPress, Odoo ERP, sistemas personalizados y más." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.adrox.top/servicios" />
        <link rel="canonical" href="https://www.adrox.top/servicios" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Servicios de Desarrollo de Software | Adrox Peru" />
        <meta name="twitter:description" content="Servicios profesionales de desarrollo: WordPress, Odoo ERP, sistemas personalizados y más." />
        <script type="application/ld+json">{JSON.stringify(servicesSchema)}</script>
      </Helmet>
      {productosSchema.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@graph': productosSchema })}</script>
        </Helmet>
      )}

      <div className="bg-white selection:bg-[#366D73]/20 selection:text-[#353E40]">
        <Navbar />
        
        {/* ============= HERO ============= */}
        <header className="relative pt-20 pb-16 sm:pb-24 overflow-hidden">
          {/* Dot grid */}
          <div
            className="absolute inset-0 -z-10"
            style={{
              backgroundImage: 'radial-gradient(#353E4014 1.5px, transparent 1.5px)',
              backgroundSize: '28px 28px',
            }}
          />
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,#07F2F20d,transparent)]" />

          <div className="mx-auto max-w-5xl px-6 lg:px-8 pt-20 text-center">
            <h1
              data-aos="fade-up"
              data-aos-delay="100"
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#353E40] leading-[1.08]"
            >
              Soluciones que{' '}
              <span className="text-[#366D73]">transforman</span>
              <br />tu empresa.
            </h1>

            <p
              data-aos="fade-up"
              data-aos-delay="150"
              className="mt-6 text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed"
            >
              Servicios de desarrollo de software de alta calidad, adaptados a las necesidades específicas de tu empresa. Desde sitios web hasta sistemas empresariales complejos.
            </p>
          </div>
        </header>

        {/* ============= SERVICIOS DINÁMICOS (Firebase) ============= */}
        <section className="border-t border-gray-100 py-16 sm:py-20">
          <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-10">
            <div className="text-center mb-12" data-aos="fade-up">
              <p className="text-xs font-semibold text-[#366D73] tracking-[0.2em] uppercase mb-3">
                Catálogo completo
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#353E40]">
                Productos y <span className="text-[#366D73]">servicios</span>
              </h2>
            </div>
            <ServiciosList />
          </div>
        </section>

        {/* ============= SERVICIOS DESTACADOS ============= */}
        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-10">
            <div className="text-center mb-12" data-aos="fade-up">
              <p className="text-xs font-semibold text-[#366D73] tracking-[0.2em] uppercase mb-3">
                Especialidades
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#353E40]">
                Servicios <span className="text-[#366D73]">Destacados</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  className="group border border-gray-100 rounded-2xl p-6 sm:p-8 hover:border-[#366D73]/30 transition-all duration-300 flex flex-col cursor-pointer"
                  data-aos="fade-up"
                  data-aos-delay={index * 80}
                  onClick={() => handleServiceClick(service.whatsappMessage)}
                >
                  <div className="w-12 h-12 rounded-full bg-[#366D73]/10 flex items-center justify-center mb-5 group-hover:bg-[#366D73]/20 transition-colors">
                    <service.icon className="h-6 w-6 text-[#366D73]" />
                  </div>

                  <h3 className="text-xl font-bold text-[#353E40] mb-3 group-hover:text-[#366D73] transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6 leading-relaxed flex-grow">
                    {service.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-400">
                        <svg className="h-4 w-4 text-[#366D73] mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="inline-flex items-center gap-1 text-sm font-semibold text-[#366D73] group-hover:gap-2 transition-all">
                    Solicitar servicio <ArrowLongRightIcon className="h-4 w-4" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============= CTA WHATSAPP ============= */}
        <section className="py-16 sm:py-20 bg-[#353E40]">
          <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-10 text-center">
            <div data-aos="fade-up">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                ¿No encuentras lo que <span className="text-[#07F2F2]">buscas</span>?
              </h2>
              <p className="mt-4 text-gray-300 text-lg max-w-xl mx-auto">
                Contáctanos directamente y cuéntanos sobre tu proyecto. Estamos listos para ayudarte con soluciones personalizadas.
              </p>
              <div className="mt-8">
                <button
                  onClick={() => handleServiceClick('Hola, me gustaría hacer una consulta sobre sus servicios')}
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-sm font-semibold text-[#353E40] hover:bg-gray-100 transition-all duration-200 hover:scale-[1.02]"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Consulta Directa por WhatsApp
                </button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}